import type {
  BusinessCenterEntityId,
  BusinessCenterEntityReference,
  BusinessCenterRoleKey,
} from "./types";
import {
  buildBusinessCenterLookupTables,
  type BusinessCenterUnifiedDataModel,
  type BusinessCenterUnifiedLookupTables,
} from "./businessCenterDataModel";

export type BusinessCenterPermissionAction =
  | "view"
  | "edit"
  | "delete"
  | "create"
  | "manage_users"
  | "access_billing"
  | "access_reports";

export type BusinessCenterAccessScope =
  | "global"
  | "company"
  | "company_read_only"
  | "assigned"
  | "assigned_or_company"
  | "portal_only"
  | "none";

export type BusinessCenterPermissionResourceType =
  | "company"
  | "user"
  | "workspace"
  | "workspace_template"
  | "workspace_assignment"
  | "lead"
  | "client"
  | "project"
  | "task"
  | "document"
  | "billing_record"
  | "portal_access"
  | "role";

export interface BusinessCenterPermissionTarget {
  entityType: BusinessCenterPermissionResourceType;
  entityId: BusinessCenterEntityId;
  companyId?: BusinessCenterEntityId | null;
  ownerUserId?: BusinessCenterEntityId | null;
  assignedUserId?: BusinessCenterEntityId | null;
  assignedCompanyId?: BusinessCenterEntityId | null;
  portalOnly?: boolean;
}

export interface BusinessCenterPermissionContext {
  role: BusinessCenterRoleKey;
  userId?: BusinessCenterEntityId | null;
  companyId?: BusinessCenterEntityId | null;
  model?: BusinessCenterUnifiedDataModel;
  lookup?: BusinessCenterUnifiedLookupTables;
}

export interface BusinessCenterPermissionDecision {
  allowed: boolean;
  action: BusinessCenterPermissionAction;
  role: BusinessCenterRoleKey;
  scope: BusinessCenterAccessScope;
  reason: string;
}

interface RolePermissionProfile {
  actions: Record<BusinessCenterPermissionAction, boolean>;
  scope: BusinessCenterAccessScope;
}

const rolePermissionProfiles: Record<BusinessCenterRoleKey, RolePermissionProfile> = {
  admin: {
    scope: "global",
    actions: {
      view: true,
      edit: true,
      delete: true,
      create: true,
      manage_users: true,
      access_billing: true,
      access_reports: true,
    },
  },
  manager: {
    scope: "company",
    actions: {
      view: true,
      edit: true,
      delete: true,
      create: true,
      manage_users: false,
      access_billing: true,
      access_reports: true,
    },
  },
  supervisor: {
    scope: "company",
    actions: {
      view: true,
      edit: true,
      delete: false,
      create: true,
      manage_users: false,
      access_billing: false,
      access_reports: true,
    },
  },
  team_member: {
    scope: "assigned",
    actions: {
      view: true,
      edit: true,
      delete: false,
      create: true,
      manage_users: false,
      access_billing: false,
      access_reports: true,
    },
  },
  client: {
    scope: "company_read_only",
    actions: {
      view: true,
      edit: false,
      delete: false,
      create: false,
      manage_users: false,
      access_billing: true,
      access_reports: false,
    },
  },
  viewer: {
    scope: "company_read_only",
    actions: {
      view: true,
      edit: false,
      delete: false,
      create: false,
      manage_users: false,
      access_billing: false,
      access_reports: true,
    },
  },
};

export const businessCenterPermissionRules = rolePermissionProfiles;

export function evaluateBusinessCenterPermission(
  context: BusinessCenterPermissionContext,
  action: BusinessCenterPermissionAction,
  target?: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null,
): BusinessCenterPermissionDecision {
  const profile = businessCenterPermissionRules[context.role];
  const normalizedTarget = normalizeTarget(context, target);
  const scope = normalizedTarget.scope;
  const allowedByAction = profile.actions[action];
  const allowedByScope = isScopeAllowed(profile.scope, scope, action);
  const allowed = allowedByAction && allowedByScope;

  return {
    allowed,
    action,
    role: context.role,
    scope,
    reason: allowed
      ? "Allowed by permission profile and company scope."
      : `Blocked by ${allowedByAction ? "scope" : "action"} rules for ${context.role}.`,
  };
}

export function canView(
  context: BusinessCenterPermissionContext,
  target?: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null,
) {
  return evaluateBusinessCenterPermission(context, "view", target).allowed;
}

export function canEdit(
  context: BusinessCenterPermissionContext,
  target?: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null,
) {
  return evaluateBusinessCenterPermission(context, "edit", target).allowed;
}

export function canDelete(
  context: BusinessCenterPermissionContext,
  target?: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null,
) {
  return evaluateBusinessCenterPermission(context, "delete", target).allowed;
}

export function canCreate(
  context: BusinessCenterPermissionContext,
  target?: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null,
) {
  return evaluateBusinessCenterPermission(context, "create", target).allowed;
}

export function canManageUsers(
  context: BusinessCenterPermissionContext,
  target?: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null,
) {
  return evaluateBusinessCenterPermission(context, "manage_users", target).allowed;
}

export function canAccessBilling(
  context: BusinessCenterPermissionContext,
  target?: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null,
) {
  return evaluateBusinessCenterPermission(context, "access_billing", target).allowed;
}

export function canAccessReports(
  context: BusinessCenterPermissionContext,
  target?: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null,
) {
  return evaluateBusinessCenterPermission(context, "access_reports", target).allowed;
}

export function buildBusinessCenterPermissionContext(
  model: BusinessCenterUnifiedDataModel,
  role: BusinessCenterRoleKey,
  userId?: BusinessCenterEntityId | null,
): BusinessCenterPermissionContext {
  return {
    role,
    userId: userId || null,
    companyId: resolveCompanyIdFromUser(model, userId || null),
    model,
    lookup: buildBusinessCenterLookupTables(model),
  };
}

export function buildBusinessCenterPermissionTarget(
  context: BusinessCenterPermissionContext,
  target: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null | undefined,
): BusinessCenterPermissionTarget {
  return normalizeTarget(context, target).target;
}

export function isBusinessCenterSameCompany(
  context: BusinessCenterPermissionContext,
  target?: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null,
) {
  const normalized = normalizeTarget(context, target);
  return normalized.scope !== "none" && normalized.companyId !== null && normalized.companyId === context.companyId;
}

export function isBusinessCenterAssignedAccess(
  context: BusinessCenterPermissionContext,
  target?: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null,
) {
  const normalized = normalizeTarget(context, target);
  return normalized.scope === "assigned" || normalized.scope === "assigned_or_company";
}

function normalizeTarget(
  context: BusinessCenterPermissionContext,
  target?: BusinessCenterPermissionTarget | BusinessCenterEntityReference | null,
): { target: BusinessCenterPermissionTarget; scope: BusinessCenterAccessScope; companyId: BusinessCenterEntityId | null } {
  if (!target) {
    return {
      target: {
        entityType: "company",
        entityId: context.companyId || "global",
        companyId: context.companyId || null,
      },
      scope: context.role === "admin" ? "global" : context.role === "manager" ? "company" : context.role === "team_member" ? "assigned" : "company_read_only",
      companyId: context.companyId || null,
    };
  }

  if ("entityType" in target && "entityId" in target && !("portalOnly" in target)) {
    return resolveTargetFromReference(context, target);
  }

  const normalizedTarget: BusinessCenterPermissionTarget = {
    entityType: target.entityType,
    entityId: target.entityId,
    companyId: target.companyId || null,
    ownerUserId: target.ownerUserId || null,
    assignedUserId: target.assignedUserId || null,
    assignedCompanyId: target.assignedCompanyId || null,
    portalOnly: target.portalOnly || false,
  };
  const scope = deriveScope(context, normalizedTarget);
  return { target: normalizedTarget, scope, companyId: normalizedTarget.companyId || null };
}

function resolveTargetFromReference(
  context: BusinessCenterPermissionContext,
  reference: BusinessCenterEntityReference,
): { target: BusinessCenterPermissionTarget; scope: BusinessCenterAccessScope; companyId: BusinessCenterEntityId | null } {
  const lookup = context.lookup || (context.model ? buildBusinessCenterLookupTables(context.model) : null);
  const companyId = reference.companyId || lookup?.entityIdToCompanyId.get(reference.entityId) || null;
  const target: BusinessCenterPermissionTarget = {
    entityType: mapReferenceType(reference.entityType),
    entityId: reference.entityId,
    companyId,
    portalOnly: reference.entityType === "portal_access",
  };

  if (lookup && context.model) {
    const record = context.model;
    const user = lookup.usersById.get(reference.entityId);
    const workspace = lookup.workspacesById.get(reference.entityId);
    const project = lookup.projectsById.get(reference.entityId);
    const task = lookup.tasksById.get(reference.entityId);
    const document = lookup.documentsById.get(reference.entityId);
    const billing = lookup.billingRecordsById.get(reference.entityId);
    const client = lookup.clientsById.get(reference.entityId);

    if (user) {
      target.ownerUserId = user.id;
      target.companyId = user.companyId;
    } else if (workspace) {
      target.companyId = workspace.companyId;
    } else if (project) {
      target.assignedCompanyId = project.clientId || null;
      target.companyId = companyId || resolveCompanyIdFromProjectLike(project.clientId, project.brand, companyId);
    } else if (task) {
      target.assignedCompanyId = task.clientId || null;
      target.companyId = companyId || resolveCompanyIdFromProjectLike(task.clientId, task.brand, companyId);
    } else if (document) {
      target.assignedCompanyId = document.clientId || null;
      target.companyId = companyId || resolveCompanyIdFromDocumentLike(document.clientId, document.projectId, companyId);
    } else if (billing) {
      target.assignedCompanyId = billing.clientId || null;
      target.companyId = companyId || billing.companyId || null;
    } else if (client) {
      target.companyId = companyId || client.id;
    } else if (record) {
      target.companyId = companyId || null;
    }
  }

  const scope = deriveScope(context, target);
  return { target, scope, companyId: target.companyId || null };
}

function deriveScope(
  context: BusinessCenterPermissionContext,
  target: BusinessCenterPermissionTarget,
): BusinessCenterAccessScope {
  if (context.role === "admin") return "global";
  if (target.portalOnly) return "portal_only";
  if (context.companyId && target.companyId && context.companyId === target.companyId) {
    if (context.role === "client" || context.role === "viewer") return "company_read_only";
    return "company";
  }
  if (context.userId && target.ownerUserId && context.userId === target.ownerUserId) {
    return "assigned";
  }
  if (context.userId && target.assignedUserId && context.userId === target.assignedUserId) {
    return "assigned";
  }
  if (context.companyId && target.assignedCompanyId && context.companyId === target.assignedCompanyId) {
    return "assigned_or_company";
  }
  return "none";
}

function isScopeAllowed(
  roleScope: BusinessCenterAccessScope,
  targetScope: BusinessCenterAccessScope,
  action: BusinessCenterPermissionAction,
) {
  if (roleScope === "global") return true;
  if (roleScope === "company") {
    return targetScope === "company" || targetScope === "company_read_only" || targetScope === "assigned" || targetScope === "assigned_or_company" || targetScope === "portal_only";
  }
  if (roleScope === "assigned") {
    return targetScope === "assigned" || targetScope === "assigned_or_company" || targetScope === "portal_only";
  }
  if (roleScope === "company_read_only") {
    if (action === "view" || action === "access_billing" || action === "access_reports") {
      return targetScope === "company" || targetScope === "company_read_only" || targetScope === "portal_only";
    }
    return false;
  }
  if (roleScope === "portal_only") {
    return targetScope === "portal_only";
  }
  return false;
}

function mapReferenceType(entityType: BusinessCenterEntityReference["entityType"]): BusinessCenterPermissionResourceType {
  return entityType === "billing_record"
    ? "billing_record"
    : entityType === "portal_access"
      ? "portal_access"
      : entityType === "workspace"
        ? "workspace"
        : entityType === "workspace_template"
          ? "workspace_template"
          : entityType === "workspace_assignment"
            ? "workspace_assignment"
            : entityType;
}

function resolveCompanyIdFromUser(
  model: BusinessCenterUnifiedDataModel,
  userId: BusinessCenterEntityId | null,
): BusinessCenterEntityId | null {
  if (!userId) return null;
  return model.users.find((user) => user.id === userId)?.companyId || null;
}

function resolveCompanyIdFromProjectLike(
  clientId: BusinessCenterEntityId | null | undefined,
  brand: string | undefined,
  fallback: BusinessCenterEntityId | null,
) {
  if (clientId) return clientId;
  if (brand) return brand;
  return fallback;
}

function resolveCompanyIdFromDocumentLike(
  clientId: BusinessCenterEntityId | null | undefined,
  projectId: BusinessCenterEntityId | null | undefined,
  fallback: BusinessCenterEntityId | null,
) {
  if (clientId) return clientId;
  if (projectId) return projectId;
  return fallback;
}
